import { motion } from "framer-motion";
import { Palette, Hand, Music, BookOpen, Users, Puzzle, Activity, Leaf } from "lucide-react";

const IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_asilo-peramore/artifacts/y299z55i_fda845fa-8459-4c47-86f5-ce73721e124b.jpeg";

const blocks = [
  { t: "Un ambiente pensato a misura di bambino", p: "Gli spazi del nostro nido sono progettati per offrire sicurezza, libertà di movimento e infinite occasioni di scoperta. Le ampie aree permettono ai bambini di gattonare, correre, saltare ed esplorare, mentre gli angoli morbidi diventano luoghi di relax, lettura, coccole e condivisione. Ogni ambiente stimola curiosità, creatività e fantasia, rispettando i tempi di crescita di ciascun bambino." },
  { t: "Imparare giocando", p: "Per noi il gioco è il principale strumento di apprendimento. Durante l'anno proponiamo un percorso ricco di esperienze, costruito attorno a un tema che accompagna i bambini alla scoperta del mondo. Ogni giornata è un'occasione per sperimentare, emozionarsi e crescere insieme." },
  { t: "Crescere con amore", p: "Il nostro obiettivo non è soltanto prenderci cura dei bambini, ma accompagnarli nella costruzione della loro autonomia, della fiducia in sé stessi e delle prime relazioni. Ogni bambino viene accolto e valorizzato nella propria individualità, favorendo lo sviluppo delle competenze emotive, cognitive, relazionali e motorie." },
  { t: "Insieme alle famiglie", p: "La collaborazione con i genitori è uno dei valori più importanti del nostro progetto. Costruiamo un rapporto basato sul dialogo, sulla fiducia e sulla condivisione, affinché famiglia ed educatrici possano accompagnare insieme ogni bambino nel suo percorso di crescita." },
];

const activities = [
  { icon: Palette, t: "Laboratori creativi di pittura, disegno e manipolazione" },
  { icon: Hand, t: "Esperienze sensoriali per stimolare tutti i sensi" },
  { icon: Music, t: "Musica, canto e movimento" },
  { icon: BookOpen, t: "Letture animate e racconti" },
  { icon: Users, t: "Giochi simbolici e di imitazione" },
  { icon: Puzzle, t: "Costruzioni, puzzle e giochi logici" },
  { icon: Activity, t: "Attività motorie e percorsi psicomotori" },
  { icon: Leaf, t: "Scoperta della natura e delle stagioni" },
];

const AB = "https://customer-assets-wrfwihn1.emergentagent.net/job_asilo-peramore/artifacts/";
const activityPhotos = [
  AB + "a12870a822bdb9a0_0985d89c-c2f3-4a52-b8ca-ea5199ce0735.jpeg",
  AB + "0e2e51eb9935ea8c_968e3030-5eed-4cc6-8721-2063425a666a.jpeg",
  AB + "94d4199f7e613f40_45fa5833-95cd-48f4-8926-051a2e314413.jpeg",
  AB + "e8d01e1fa4ebe081_2a6783ce-cb60-49ce-a4a2-dee441f88ed1.jpeg",
  AB + "a0357151a4a13f57_84621408-d8e1-4447-a175-c5b41d96b125.jpeg",
  AB + "786d7bf056cc59ec_81fd3519-43cf-45ca-b541-d132825a345d.jpeg",
  AB + "83f8a30ea2655fa1_ff16c1c9-5efe-4f22-ae36-ba737be22931.webp",
  AB + "2a87b8fdf2c09029_b77b37c1-b5f3-4512-9047-494dcb58b514.webp",
  AB + "2fea9dc7c981cf2b_d039a889-e5ae-4ad5-a718-7cd1b79a946f.jpeg",
];

export const Progetto = () => (
  <section id="progetto" data-testid="progetto-section" className="py-20 sm:py-28 scroll-mt-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-sm font-800 uppercase tracking-[0.2em] text-emerald-500">Il Nostro Progetto Educativo</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl md:text-5xl font-700 text-slate-800">Ogni bambino è unico</h2>
          <div className="mt-6 space-y-4 text-slate-600 font-600 leading-relaxed text-base sm:text-lg">
            <p>All'Asilo Nido Per Amore crediamo che ogni bambino meriti di crescere in un ambiente capace di accoglierlo con amore, rispetto e attenzione.</p>
            <p>Il nostro progetto nasce dall'ascolto dei bisogni autentici dei bambini e delle loro famiglie. Ogni scelta, dagli spazi alle attività, è pensata per favorire il benessere, la serenità e uno sviluppo armonioso, affinché ogni bambino possa tornare a casa felice e desideroso di raccontare la propria giornata.</p>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <img src={IMG} alt="Progetto educativo" className="w-full h-[300px] sm:h-[440px] object-cover rounded-[2rem] shadow-xl" />
        </motion.div>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {blocks.map((b) => (
          <div key={b.t} className="rounded-3xl bg-[#f7f0e3] p-7">
            <h3 className="font-heading text-xl font-700 text-slate-800">{b.t}</h3>
            <p className="mt-3 text-slate-600 font-600 leading-relaxed">{b.p}</p>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h3 className="font-heading text-2xl font-700 text-slate-800 text-center">Le nostre attività</h3>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {activities.map((a) => (
            <div key={a.t} data-testid={`attivita-${a.t}`} className="flex flex-col items-center text-center gap-3 rounded-3xl bg-white border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
              <span className="grid place-items-center h-12 w-12 rounded-2xl bg-rose-100 text-rose-500">
                <a.icon className="h-6 w-6" />
              </span>
              <p className="text-sm font-700 text-slate-700">{a.t}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {activityPhotos.map((src, i) => (
            <div
              key={i}
              data-testid={`attivita-foto-${i}`}
              className={`overflow-hidden rounded-2xl group shadow-sm ${i === 0 ? "col-span-2 sm:col-span-1" : ""}`}
            >
              <img
                src={src}
                alt={`Attività dell'asilo ${i + 1}`}
                loading="lazy"
                className="w-full h-48 sm:h-56 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
